import { CalendarClock, FileText, CreditCard, CalendarPlus } from "lucide-react";

// Parent Dashboard -> Quick Actions grid. Extracted from ParentDashboard.js.
// Builds its own action list internally (no memoization needed for 4 static
// items) which also removes the previous top-level useMemo that had a real
// react-hooks/exhaustive-deps warning in the original component.
//
// v1.3 Presentation Mode polish: "Make a Payment" and "Complete Required
// Forms" always surface a status pill — "$0.00" / "0 Remaining" instead of
// a blank card — so a parent can tell their status at a glance without
// scrolling further down the page, even when everything is already done.
export const QuickActions = ({ pendingFormsCount, balanceTotal, onSchedule, onReschedule, onPayment, onForms }) => {
  const formsLabel = `${pendingFormsCount || 0} Remaining`;
  const balanceLabel = `$${(balanceTotal || 0).toFixed(2)}`;

  const quickActions = [
    { key: "schedule", label: "Schedule Driving Lesson", icon: CalendarPlus, onClick: onSchedule, testId: "quick-action-schedule-lesson" },
    { key: "reschedule", label: "Request Reschedule", icon: CalendarClock, onClick: onReschedule, testId: "quick-action-request-reschedule" },
    {
      key: "payment", label: "Make a Payment", icon: CreditCard, onClick: onPayment, testId: "quick-action-make-payment",
      badge: balanceLabel, badgeTone: balanceTotal > 0 ? "attention" : "neutral",
    },
    {
      key: "forms", label: "Complete Required Forms", icon: FileText, onClick: onForms, testId: "quick-action-complete-forms",
      badge: formsLabel, badgeTone: pendingFormsCount > 0 ? "attention" : "neutral",
    },
  ];

  return (
    <section className="rounded-[var(--radius)] border bg-card p-4 sm:p-5" data-anchor="quick-actions" data-testid="parent-quick-actions">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Quick Actions</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {quickActions.map((qa) => (
          <button
            key={qa.key}
            onClick={qa.onClick}
            className="relative flex flex-col items-center gap-2 rounded-[var(--radius)] border p-3 sm:p-4 text-center hover:bg-accent hover:border-[hsl(var(--role-accent))]/40 transition-colors min-h-[88px] justify-center"
            data-testid={qa.testId}
          >
            {qa.badge ? (
              <span
                className={`absolute top-1.5 right-1.5 h-5 px-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap flex items-center justify-center ${
                  qa.badgeTone === "attention"
                    ? "bg-[hsl(var(--role-accent))] text-white"
                    : "bg-secondary text-muted-foreground border border-border"
                }`}
                data-testid={`${qa.testId}-badge`}
              >
                {qa.badge}
              </span>
            ) : null}
            <qa.icon className="h-5 w-5 text-[hsl(var(--role-accent))]" />
            <span className="text-xs font-medium leading-tight">{qa.label}</span>
          </button>
        ))}
      </div>
    </section>
  );
};
